import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { GripVertical, Pencil, Trash2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';
import '../styles/Contacts.css';

interface PackageLocation {
  _id: string;
  name: string;
  projectId: string;
  projectName?: string;
  order: number;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const PackageLocations: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [locations, setLocations] = useState<PackageLocation[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  // Add/Edit modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<PackageLocation | null>(null);
  const [locationName, setLocationName] = useState('');
  const [locationOrder, setLocationOrder] = useState<number>(0);
  const [saving, setSaving] = useState(false);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (projects.length > 0 && !projectFilter) {
      setProjectFilter(projects[0]._id);
    }
  }, [projects]);

  useEffect(() => {
    if (workspaceId && projectFilter) {
      fetchLocations();
    }
  }, [workspaceId, pagination.page, searchQuery, projectFilter]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchLocations = async (showTableLoader = false) => {
    if (!workspaceId) return;

    try {
      if (showTableLoader) {
        setTableLoading(true);
      } else {
        setInitialLoading(true);
      }
      setError('');

      const response = await api.get(`/workspaces/${workspaceId}/pkglocations`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          projectId: projectFilter || undefined,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });

      setLocations(response.data.locations);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch locations');
      console.error('Error fetching locations:', err);
    } finally {
      setInitialLoading(false);
      setTableLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    if (projects.length > 0) {
      setProjectFilter(projects[0]._id);
    }
    setPagination({ ...pagination, page: 1 });
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newLocations = [...locations];
    const [draggedLocation] = newLocations.splice(draggedIndex, 1);
    newLocations.splice(dropIndex, 0, draggedLocation);

    // Update local state immediately for better UX
    setLocations(newLocations);
    setDraggedIndex(null);
    setDragOverIndex(null);

    try {
      setIsReordering(true);

      // Update all orders with a single API call
      const updatedLocations = newLocations.map((location, index) => ({
        _id: location._id,
        order: index
      }));

      await api.post(
        `/workspaces/${workspaceId}/pkglocations/bulk-update-order`,
        { locations: updatedLocations }
      );

      toast.success('Location order updated');

      // Refresh the list to get the updated data
      await fetchLocations(true);
    } catch (err: any) {
      console.error('Error updating location order:', err);
      toast.error('Failed to update order');
      // Revert on error by refetching
      await fetchLocations(true);
    } finally {
      setIsReordering(false);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setLocationName('');
    setLocationOrder(locations.length);
    setIsModalOpen(true);
  };

  const handleEditLocation = (location: PackageLocation) => {
    setEditingLocation(location);
    setLocationName(location.name);
    setLocationOrder(location.order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLocation(null);
    setLocationName('');
    setLocationOrder(0);
  };

  const handleSaveLocation = async () => {
    if (!locationName.trim()) {
      toast.error('Location name is required');
      return;
    }

    if (!projectFilter) {
      toast.error('Please select a project');
      return;
    }

    try {
      setSaving(true);

      if (editingLocation) {
        await api.put(`/workspaces/${workspaceId}/pkglocations/${editingLocation._id}`, {
          name: locationName.trim(),
          order: locationOrder
        });
        toast.success('Location updated successfully');
      } else {
        await api.post(`/workspaces/${workspaceId}/pkglocations`, {
          name: locationName.trim(),
          projectId: projectFilter,
          order: locationOrder
        });
        toast.success('Location added successfully');
      }

      handleCloseModal();
      fetchLocations(true);
    } catch (err: any) {
      console.error('Error saving location:', err);
      toast.error(err.response?.data?.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLocation = async (location: PackageLocation) => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/workspaces/${workspaceId}/pkglocations/${location._id}`);
      toast.success('Location deleted successfully');
      fetchLocations(true);
    } catch (err: any) {
      console.error('Error deleting location:', err);
      toast.error(err.response?.data?.message || 'Failed to delete location');
    }
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Package Locations</h2>
                <p className="builders-section-subtitle">
                  Manage package locations across projects
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline btn-md" onClick={handleClearFilters}>
                  Clear
                </button>
                <button className="btn btn-primary btn-md" onClick={handleAddLocation}>
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="builders-error" style={{ marginBottom: '20px' }}>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {initialLoading ? (
          <div className="builders-container">
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
              <select
                className="form-input"
                value={projectFilter}
                onChange={handleProjectFilterChange}
              >
                {projects.map(project => (
                  <option key={project._id} value={project._id}>{project.name}</option>
                ))}
              </select>

              <input
                type="text"
                className="form-input"
                placeholder="Search locations..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            {/* Locations Table */}
            <div className="data-table-container" style={{ position: 'relative' }}>
              {/* Loading Overlay */}
              {(tableLoading || isReordering) && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10
                }}>
                  <div className="contacts-loading-spinner"></div>
                </div>
              )}

              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Name</th>
                    <th>Order</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locations.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="data-table-empty">
                        No locations found. Click "Add" to create one.
                      </td>
                    </tr>
                  ) : (
                    locations.map((location, index) => (
                      <tr
                        key={location._id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          opacity: draggedIndex === index ? 0.4 : 1,
                          backgroundColor: dragOverIndex === index ? '#F3F4F6' : undefined,
                          cursor: 'move'
                        }}
                      >
                        <td style={{ cursor: 'grab', padding: '8px' }}>
                          <GripVertical size={20} color="#9CA3AF" />
                        </td>
                        <td className="data-table-primary-cell">{location.name}</td>
                        <td>{location.order}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
                            <button
                              className="btn-icon"
                              onClick={() => handleEditLocation(location)}
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              className="btn-icon btn-icon-danger"
                              onClick={() => handleDeleteLocation(location)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="builders-pagination" style={{ marginTop: '20px' }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary btn-sm"
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="btn btn-secondary btn-sm"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add/Edit Modal */}
        {isModalOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '500px',
              maxWidth: '90%'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                  {editingLocation ? 'Edit Location' : 'Add Location'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '0',
                    color: '#6B7280'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Project : {projects.find(p => p._id === projectFilter)?.name || 'N/A'}
                </label>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Order:
                </label>
                <input
                  type="number"
                  className="form-input"
                  value={locationOrder}
                  onChange={(e) => setLocationOrder(parseInt(e.target.value) || 0)}
                  disabled
                  style={{ backgroundColor: '#F3F4F6' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
                  Name <span style={{ color: 'red' }}>*</span>:
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Name"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button
                  className="btn btn-secondary btn-md"
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-md"
                  onClick={handleSaveLocation}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingLocation ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    </AppLayout>
  );
};

export default PackageLocations;
